/**
 * Cadeia logística canónica de chegada e de partida.
 *
 * Regras vinculativas:
 * - a chegada começa exatamente na hora declarada pela pessoa;
 * - `airport_transfer` tem identidade própria por direção (`arrival` | `departure`);
 * - `check_in`/`check_out` só existem com hotel realmente selecionado E com
 *   logística de aeroporto ativa;
 * - "criar sem logística" limpa aeroportos/margens/transferências/check-in mas
 *   NUNCA apaga o hotel escolhido manualmente;
 * - paragens noturnas só entram quando o pedido as menciona claramente;
 * - módulo puro: sem IO, sem Places/Routes, sem relógio, sem `Math.random`.
 */

import {
  AIRPORT_DEPARTURE_MARGIN_MIN,
  HOTEL_CHECKIN_MIN,
  LANDING_BAGGAGE_MIN,
  type TravelLogistics,
} from "./arrival-window";

export type LogisticsEntryKind =
  | "arrival"
  | "arrival_overhead"
  | "airport_transfer"
  | "check_in"
  | "check_out"
  | "airport_buffer"
  | "free_time";

export type LogisticsEntry = {
  id: string;
  kind: LogisticsEntryKind;
  day: number;
  startMinutes: number;
  endMinutes: number;
  /** Só existe em `airport_transfer`. */
  direction?: "arrival" | "departure";
  /** Deslocação real usada na transferência (nunca inventada). */
  transfer?: { durationMinutes: number; distanceMeters: number };
};

/** Transferência real calculada para o modo escolhido. Nunca estimada no Preview. */
export type ResolvedTransfer = {
  durationMinutes: number;
  distanceMeters: number;
};

export type ArrivalChainInput = {
  /** Hora declarada de aterragem (minutos do dia). */
  arrivalMinutes: number;
  /** Aeroporto de chegada resolvido (nome não vazio). */
  arrivalAirport?: string | null;
  /** Hotel realmente selecionado pela pessoa. */
  hotelPlaceId?: string | null;
  /** Deslocação real aeroporto → hotel para o modo escolhido. */
  transfer?: ResolvedTransfer | null;
  /** A pessoa escolheu "criar roteiro sem logística". */
  skipLogistics?: boolean;
};

export type ChainResult = {
  entries: LogisticsEntry[];
  /** Minutos do dia a partir dos quais a primeira visita pode começar. */
  firstActivityMinutes: number | null;
  /** A cadeia está incompleta por falta de deslocação real calculada. */
  incomplete: boolean;
};

const airportResolved = (name?: string | null) => Boolean(name && name.trim().length >= 3);

/**
 * Cadeia do Dia 1: chegada → margem de desembarque/bagagem → transferência
 * aeroporto→hotel → check-in. Sem aeroporto resolvido não se inventa nada.
 */
export function buildArrivalChain(input: ArrivalChainInput): ChainResult {
  if (input.skipLogistics || !airportResolved(input.arrivalAirport)) {
    return { entries: [], firstActivityMinutes: null, incomplete: false };
  }
  const entries: LogisticsEntry[] = [];
  let cursor = Math.max(0, Math.round(input.arrivalMinutes));

  entries.push({ id: "d1-arrival", kind: "arrival", day: 1, startMinutes: cursor, endMinutes: cursor });
  const overheadEnd = cursor + LANDING_BAGGAGE_MIN;
  entries.push({
    id: "d1-arrival-overhead",
    kind: "arrival_overhead",
    day: 1,
    startMinutes: cursor,
    endMinutes: overheadEnd,
  });
  cursor = overheadEnd;

  // Sem deslocação real calculada não se mostra duração inventada.
  if (!input.transfer || !(input.transfer.durationMinutes > 0)) {
    return { entries, firstActivityMinutes: null, incomplete: true };
  }
  const transferEnd = cursor + Math.round(input.transfer.durationMinutes);
  entries.push({
    id: "d1-airport-transfer",
    kind: "airport_transfer",
    day: 1,
    direction: "arrival",
    startMinutes: cursor,
    endMinutes: transferEnd,
    transfer: {
      durationMinutes: Math.round(input.transfer.durationMinutes),
      distanceMeters: Math.round(input.transfer.distanceMeters),
    },
  });
  cursor = transferEnd;

  if (input.hotelPlaceId) {
    const checkinEnd = cursor + HOTEL_CHECKIN_MIN;
    entries.push({ id: "d1-check-in", kind: "check_in", day: 1, startMinutes: cursor, endMinutes: checkinEnd });
    cursor = checkinEnd;
  }
  return { entries, firstActivityMinutes: cursor, incomplete: false };
}

export type DepartureChainInput = {
  /** Último dia da viagem. */
  day: number;
  /** Hora declarada do voo (minutos do dia). */
  departureMinutes: number;
  departureAirport?: string | null;
  hotelPlaceId?: string | null;
  /** Deslocação real hotel/cidade → aeroporto. */
  transfer?: ResolvedTransfer | null;
  skipLogistics?: boolean;
};

export type DepartureChainResult = ChainResult & {
  /** Minutos do dia a partir dos quais não pode existir mais atividade. */
  activityCutoffMinutes: number | null;
};

/**
 * Cadeia do último dia: check-out → transferência de partida → margem de
 * aeroporto. Nada de visitas ou refeições depois do início do bloco.
 */
export function buildDepartureChain(input: DepartureChainInput): DepartureChainResult {
  if (input.skipLogistics || !airportResolved(input.departureAirport)) {
    return { entries: [], firstActivityMinutes: null, incomplete: false, activityCutoffMinutes: null };
  }
  if (!input.transfer || !(input.transfer.durationMinutes > 0)) {
    return { entries: [], firstActivityMinutes: null, incomplete: true, activityCutoffMinutes: null };
  }
  const day = Math.max(1, Math.trunc(input.day));
  const transferMinutes = Math.round(input.transfer.durationMinutes);
  const bufferStart = Math.round(input.departureMinutes) - AIRPORT_DEPARTURE_MARGIN_MIN;
  const transferStart = bufferStart - transferMinutes;
  const checkoutStart = input.hotelPlaceId ? transferStart - HOTEL_CHECKIN_MIN : transferStart;

  const entries: LogisticsEntry[] = [];
  if (input.hotelPlaceId) {
    entries.push({
      id: `d${day}-check-out`,
      kind: "check_out",
      day,
      startMinutes: checkoutStart,
      endMinutes: transferStart,
    });
  }
  entries.push({
    id: `d${day}-airport-transfer`,
    kind: "airport_transfer",
    day,
    direction: "departure",
    startMinutes: transferStart,
    endMinutes: bufferStart,
    transfer: { durationMinutes: transferMinutes, distanceMeters: Math.round(input.transfer.distanceMeters) },
  });
  entries.push({
    id: `d${day}-airport-buffer`,
    kind: "airport_buffer",
    day,
    startMinutes: bufferStart,
    endMinutes: Math.round(input.departureMinutes),
  });
  return {
    entries,
    firstActivityMinutes: null,
    incomplete: false,
    activityCutoffMinutes: checkoutStart,
  };
}

/**
 * Estado logístico da interface. O hotel é uma decisão independente da
 * logística de aeroporto.
 */
export type LogisticsSelection = {
  arrivalAirport?: string | null;
  departureAirport?: string | null;
  hotelPlaceId?: string | null;
  skipLogistics?: boolean;
};

/**
 * "Ainda não sei — criar roteiro sem logística": limpa aeroportos e tudo o que
 * deles deriva; preserva o hotel escolhido manualmente.
 */
export function clearAirportLogistics(current: LogisticsSelection): LogisticsSelection {
  return {
    arrivalAirport: null,
    departureAirport: null,
    hotelPlaceId: current.hotelPlaceId ?? null,
    skipLogistics: true,
  };
}

/** Aviso obrigatório no topo de um roteiro sem logística. */
export function noLogisticsNotice(lang = "pt"): string {
  const dict: Record<string, string> = {
    pt: "Roteiro sem logística de chegada/partida — não inclui aeroporto, malas, transfer nem hotel.",
    en: "Itinerary without arrival/departure logistics — no airport, luggage, transfer or hotel.",
    es: "Itinerario sin logística de llegada/salida — sin aeropuerto, equipaje, traslado ni hotel.",
    fr: "Itinéraire sans logistique d'arrivée/départ — sans aéroport, bagages, transfert ni hôtel.",
    it: "Itinerario senza logistica di arrivo/partenza — senza aeroporto, bagagli, transfer o hotel.",
    de: "Route ohne An-/Abreiselogistik — ohne Flughafen, Gepäck, Transfer oder Hotel.",
  };
  return dict[(lang || "pt").slice(0, 2).toLowerCase()] ?? dict.pt;
}

/** Placeholder genérico do campo de aeroporto — nunca um aeroporto anterior. */
export function airportPlaceholder(destination?: string | null, lang = "pt"): string {
  const city = (destination ?? "").trim();
  const dict: Record<string, string> = {
    pt: city ? `Aeroporto de chegada em ${city}` : "Aeroporto de chegada",
    en: city ? `Arrival airport in ${city}` : "Arrival airport",
    es: city ? `Aeropuerto de llegada en ${city}` : "Aeropuerto de llegada",
    fr: city ? `Aéroport d'arrivée à ${city}` : "Aéroport d'arrivée",
    it: city ? `Aeroporto di arrivo a ${city}` : "Aeroporto di arrivo",
    de: city ? `Ankunftsflughafen in ${city}` : "Ankunftsflughafen",
  };
  return dict[(lang || "pt").slice(0, 2).toLowerCase()] ?? dict.pt;
}

const NIGHT_INTENT_RE =
  /\b(vida\s+noturna|noturnas?|noturno|[àa]\s+noite|de\s+noite|nightlife|night\s+(?:photos|shots|views|life)|by\s+night|at\s+night|vida\s+nocturna|por\s+la\s+noche|de\s+nuit|bei\s+nacht)\b/i;

const fold = (v: string) => (v ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "");

/** O pedido menciona claramente noite / fotos noturnas / vida noturna? */
export function requestsNightStops(text: string): boolean {
  return NIGHT_INTENT_RE.test(fold(text ?? ""));
}

/**
 * Paragens noturnas exteriores: no máximo duas, só com intenção explícita e só
 * entre o fim do jantar e o fim do dia.
 */
export function selectNightStops<T extends { eveningEligible?: boolean; visitMinutes?: number }>(
  candidates: readonly T[],
  ctx: { requestText: string; dinnerEndMinutes: number; dayEndMinutes: number },
): T[] {
  if (!requestsNightStops(ctx.requestText)) return [];
  const out: T[] = [];
  let cursor = ctx.dinnerEndMinutes;
  for (const candidate of candidates) {
    if (out.length >= 2) break;
    if (!candidate.eveningEligible) continue;
    const end = cursor + Math.max(1, Math.round(candidate.visitMinutes ?? 45));
    if (end > ctx.dayEndMinutes) break;
    out.push(candidate);
    cursor = end;
  }
  return out;
}

/** Logística efetiva depois de aplicar a escolha da pessoa. */
export function effectiveLogistics(
  logistics: TravelLogistics,
  selection: LogisticsSelection,
): TravelLogistics {
  if (!selection.skipLogistics && !logistics.skipLogistics) return logistics;
  const { arrival: _arrival, departure: _departure, ...rest } = logistics;
  return { ...rest, skipLogistics: true, hotelSelected: Boolean(selection.hotelPlaceId) };
}
